import nexatFieldModeIconUrl from '@/assets/icons/nexat_field_mode.svg'
import nexatStreetModeIconUrl from '@/assets/icons/nexat_street_mode.svg'
import switchOverModeIconUrl from '@/assets/icons/switch_over_mode.svg'
import { bearingToIconAngle } from './vehicleGpsArrowIcon'

const ICON_SIZE = 128
const ICON_CANVAS_PAD = 4
const EDGE_FRINGE_ALPHA = 180
const EDGE_FRINGE_STRIP_PX = 5

let loadPromise = null
let cachedAtlas = null

function removeEdgeFringe(canvas, size) {
    const ctx = canvas.getContext('2d')
    const imageData = ctx.getImageData(0, 0, size, size)
    const { data } = imageData

    let minX = size
    let minY = size
    let maxX = 0
    let maxY = 0

    for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
            if (data[(y * size + x) * 4 + 3] > 12) {
                minX = Math.min(minX, x)
                maxX = Math.max(maxX, x)
                minY = Math.min(minY, y)
                maxY = Math.max(maxY, y)
            }
        }
    }

    if (maxX < minX || maxY < minY) {
        return
    }

    for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
            const nearEdge = x <= minX + EDGE_FRINGE_STRIP_PX
                || x >= maxX - EDGE_FRINGE_STRIP_PX
                || y <= minY + EDGE_FRINGE_STRIP_PX
                || y >= maxY - EDGE_FRINGE_STRIP_PX

            if (!nearEdge) {
                continue
            }

            const i = (y * size + x) * 4
            if (data[i + 3] < EDGE_FRINGE_ALPHA) {
                data[i + 3] = 0
                data[i] = 0
                data[i + 1] = 0
                data[i + 2] = 0
            }
        }
    }

    ctx.putImageData(imageData, 0, 0)
}

function loadSvgToCanvas(url, size) {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size

            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, size, size)

            const inner = size - ICON_CANVAS_PAD * 2
            const scale = Math.min(inner / img.naturalWidth, inner / img.naturalHeight)
            const width = img.naturalWidth * scale
            const height = img.naturalHeight * scale
            const x = (size - width) / 2
            const y = (size - height) / 2

            ctx.drawImage(img, x, y, width, height)
            removeEdgeFringe(canvas, size)
            resolve(canvas)
        }
        img.onerror = reject
        img.src = url
    })
}

function iconMappingEntry(x) {
    return {
        x,
        y: 0,
        width: ICON_SIZE,
        height: ICON_SIZE,
        mask: false,
        anchorX: ICON_SIZE / 2,
        anchorY: ICON_SIZE / 2,
    }
}

/** deck.gl atlas for vehicle mode SVGs (field / street / switch-over). */
export function ensureVehicleFieldModeIcon() {
    if (cachedAtlas) {
        return Promise.resolve(cachedAtlas)
    }

    if (!loadPromise) {
        loadPromise = Promise.all([
            loadSvgToCanvas(nexatFieldModeIconUrl, ICON_SIZE),
            loadSvgToCanvas(nexatStreetModeIconUrl, ICON_SIZE),
            loadSvgToCanvas(switchOverModeIconUrl, ICON_SIZE),
        ]).then(([fieldCanvas, streetCanvas, switchOverCanvas]) => {
            const atlas = document.createElement('canvas')
            atlas.width = ICON_SIZE * 3
            atlas.height = ICON_SIZE

            const ctx = atlas.getContext('2d')
            ctx.drawImage(fieldCanvas, 0, 0)
            ctx.drawImage(streetCanvas, ICON_SIZE, 0)
            ctx.drawImage(switchOverCanvas, ICON_SIZE * 2, 0)

            cachedAtlas = {
                atlas,
                mapping: {
                    field: iconMappingEntry(0),
                    street: iconMappingEntry(ICON_SIZE),
                    switch_over: iconMappingEntry(ICON_SIZE * 2),
                },
            }
            return cachedAtlas
        })
    }

    return loadPromise
}

if (import.meta.hot) {
    import.meta.hot.dispose(() => {
        cachedAtlas = null
        loadPromise = null
    })
}

/** Street SVG is wide — compensate atlas cell fill vs field (~2.45 matches visual size). */
export function vehicleIconMapSizeScale(iconKey) {
    if (iconKey === 'street') {
        return 2.0
    }
    if (iconKey === 'switch_over') {
        return 1.1
    }
    return 1
}

/** GPS heading is frame orientation; SVG arrow is travel direction (+90° in field mode). */
export const FIELD_MODE_HEADING_OFFSET = 90

export function fieldModeIconAngle(point) {
    const heading = Number(point?.heading)
    const bearing = Number.isFinite(heading)
        ? (heading + FIELD_MODE_HEADING_OFFSET + 360) % 360
        : (point?.segmentBearing ?? 0)

    return bearingToIconAngle(bearing)
}
